import { useState, useEffect, useMemo, useCallback } from 'react'
import { Card, CardBody, CardHeader, Button, Spinner } from '@heroui/react'

import {
  RecommendationLogsService,
  type RecommendationLogEntry,
} from '../services'

interface RecommendationMetrics {
  totalRequests: number
  averageProcessingTime: number
  averageDocumentsEvaluated: number
  averageDocumentsSelected: number
  selectionEfficiency: number // selected/evaluated ratio
  successRate: number
  averageCost: number
  totalCost: number
  timingBreakdown: {
    averageDocumentFetchTime: number
    averageAiProcessingTime: number
  }
}

export function RecommendationAnalytics() {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [logs, setLogs] = useState<RecommendationLogEntry[]>([])
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d'>('7d')

  const fetchLogs = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const startDate = new Date()
      if (timeRange === '24h') {
        startDate.setHours(startDate.getHours() - 24)
      } else if (timeRange === '7d') {
        startDate.setDate(startDate.getDate() - 7)
      } else {
        startDate.setDate(startDate.getDate() - 30)
      }

      const result = await RecommendationLogsService.getLogs({
        limit: 1000,
        startDate,
        endDate: new Date(),
        isTestRun: false, // Only production logs for analytics
      })

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to fetch logs')
      }
      setLogs(result.data.logs)
    } catch (err) {
      console.error('Failed to fetch logs:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch logs')
    } finally {
      setIsLoading(false)
    }
  }, [timeRange])

  useEffect(() => {
    fetchLogs()
  }, [timeRange, fetchLogs])

  // Calculate recommendation metrics
  const metrics = useMemo((): RecommendationMetrics | null => {
    if (!logs.length) return null

    const totalRequests = logs.length

    // Processing times
    const processingTimes = logs
      .map((log) => log.performance?.processingTimeMs)
      .filter((time): time is number => time != null)

    // Document metrics from unified analysis
    const documentsEvaluated = logs
      .map((log) => log.processing?.unifiedAnalysis?.totalDocumentsEvaluated)
      .filter((count): count is number => count != null)

    const documentsSelected = logs
      .map((log) => log.processing?.unifiedAnalysis?.finalRecommendationCount)
      .filter((count): count is number => count != null)

    // Success rate
    const successfulRequests = logs.filter((log) => log.success).length

    // Cost analysis
    const costs = logs
      .map((log) =>
        parseFloat(
          log.performance?.tokenUsage?.processing?.estimated_cost_usd?.total ||
            '0'
        )
      )
      .filter((cost) => cost > 0)

    // Timing breakdown analysis
    const documentFetchTimes = logs
      .map((log) => log.performance?.timingBreakdown?.documentFetchMs)
      .filter((time): time is number => time != null)

    const aiProcessingTimes = logs
      .map((log) => log.performance?.timingBreakdown?.aiProcessingMs)
      .filter((time): time is number => time != null)

    const average = (arr: number[]) =>
      arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0
    const sum = (arr: number[]) =>
      arr.length > 0 ? arr.reduce((a, b) => a + b, 0) : 0

    return {
      totalRequests,
      averageProcessingTime: average(processingTimes),
      averageDocumentsEvaluated: average(documentsEvaluated),
      averageDocumentsSelected: average(documentsSelected),
      selectionEfficiency:
        average(documentsEvaluated) > 0
          ? (average(documentsSelected) / average(documentsEvaluated)) * 100
          : 0,
      successRate:
        totalRequests > 0 ? (successfulRequests / totalRequests) * 100 : 0,
      averageCost: average(costs),
      totalCost: sum(costs),
      timingBreakdown: {
        averageDocumentFetchTime: average(documentFetchTimes),
        averageAiProcessingTime: average(aiProcessingTimes),
      },
    }
  }, [logs])

  const formatTime = (ms: number): string => {
    if (ms < 1000) return `${ms.toFixed(0)}ms`
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
    return `${(ms / 60000).toFixed(1)}m`
  }

  const formatCost = (cost: number): string => {
    return `$${cost.toFixed(4)}`
  }

  if (isLoading) {
    return (
      <Card>
        <CardBody className="flex items-center justify-center py-8">
          <Spinner size="lg" />
          <p className="mt-4">Loading recommendation analytics...</p>
        </CardBody>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardBody className="text-center py-8">
          <p className="text-red-500 mb-4">{error}</p>
          <Button color="primary" onPress={fetchLogs}>
            Retry
          </Button>
        </CardBody>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <h3 className="text-lg font-semibold">Recommendation Analytics</h3>
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                {['24h', '7d', '30d'].map((range) => (
                  <Button
                    key={range}
                    size="sm"
                    variant={timeRange === range ? 'solid' : 'bordered'}
                    onPress={() => setTimeRange(range as '24h' | '7d' | '30d')}
                  >
                    {range}
                  </Button>
                ))}
              </div>
              <Button size="sm" variant="bordered" onPress={fetchLogs}>
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {metrics ? (
            <div className="space-y-6">
              {/* Overview Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {metrics.totalRequests}
                  </div>
                  <div className="text-sm text-default-500">Total Requests</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {metrics.successRate.toFixed(1)}%
                  </div>
                  <div className="text-sm text-default-500">Success Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-purple-600">
                    {formatTime(metrics.averageProcessingTime)}
                  </div>
                  <div className="text-sm text-default-500">
                    Avg Processing Time
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-orange-600">
                    {formatCost(metrics.averageCost)}
                  </div>
                  <div className="text-sm text-default-500">Avg Cost</div>
                </div>
              </div>

              {/* Document Processing Stats */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-lg font-bold text-indigo-600">
                    {metrics.averageDocumentsEvaluated.toFixed(0)}
                  </div>
                  <div className="text-sm text-default-500">
                    Avg Documents Evaluated
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-cyan-600">
                    {metrics.averageDocumentsSelected.toFixed(1)}
                  </div>
                  <div className="text-sm text-default-500">
                    Avg Documents Selected
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-pink-600">
                    {metrics.selectionEfficiency.toFixed(1)}%
                  </div>
                  <div className="text-sm text-default-500">
                    Selection Efficiency
                  </div>
                </div>
              </div>

              {/* Timing Breakdown */}
              {(metrics.timingBreakdown.averageDocumentFetchTime > 0 ||
                metrics.timingBreakdown.averageAiProcessingTime > 0) && (
                <div>
                  <h4 className="text-md font-semibold mb-3">
                    Timing Breakdown
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-lg font-bold text-violet-600">
                        {formatTime(
                          metrics.timingBreakdown.averageDocumentFetchTime
                        )}
                      </div>
                      <div className="text-sm text-default-500">
                        Avg Document Fetch
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-teal-600">
                        {formatTime(
                          metrics.timingBreakdown.averageAiProcessingTime
                        )}
                      </div>
                      <div className="text-sm text-default-500">
                        Avg AI Processing
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Cost Analysis */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600">
                    {formatCost(metrics.totalCost)}
                  </div>
                  <div className="text-sm text-default-500">Total Cost</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600">
                    {formatCost(metrics.averageCost)}
                  </div>
                  <div className="text-sm text-default-500">Average Cost</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-default-500">
                No recommendation data available for the selected time range.
              </p>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  )
}
